import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Phone, Trash2, Users, ShieldCheck, UserCog, Mail, Building2, Pencil, X, Info, ChevronDown, Cake, Bell, Gavel } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Card, Btn, Input, Badge, TabBar } from "@/components/kit";

const ROLE_OPTIONS = [
  { value: "rep", label: "Representante" },
  { value: "manager", label: "Gestor" },
  { value: "owner", label: "CEO" },
  { value: "admin", label: "Admin" },
];
const ROLE_LABEL = { rep: "Representante", manager: "Gestor", owner: "CEO", admin: "Admin", superadmin: "Superadmin", operador: "Operador" };

const DEPT_OPTIONS = [
  { value: "commercial", label: "Comercial" },
  { value: "financial", label: "Financeiro" },
  { value: "hr", label: "RH" },
  { value: "legal", label: "Jurídico" },
  { value: "development", label: "Desenvolvimento" },
  { value: "operations", label: "Operações" },
  { value: "marketing", label: "Marketing" },
  { value: "support", label: "Suporte" },
  { value: "administrative", label: "Administrativo" },
];
const DEPT_PT = {
  commercial: "Comercial", financial: "Financeiro", hr: "RH", legal: "Jurídico",
  development: "Desenvolvimento", operations: "Operações", marketing: "Marketing",
  support: "Suporte", administrative: "Administrativo", admin: "Administrativo",
};
const deptLabel = (d) => DEPT_PT[d] || d || "—";

// Setores que a pessoa pode RECEBER no WhatsApp (opt-in). Hoje só "comercial" está ligado à
// entrega real (Radar de licitações + Gerente Comercial). Os demais ficam guardados p/ o futuro.
const NOTIFY_SECTORS = [
  { key: "comercial", label: "Comercial", desc: "Radar de licitações + acompanhamentos da Gerente Comercial", ready: true },
  { key: "financeiro", label: "Financeiro", desc: "Alertas e resumos financeiros", ready: false },
  { key: "rh", label: "RH", desc: "Avisos de RH", ready: false },
  { key: "juridico", label: "Jurídico", desc: "Alertas jurídicos", ready: false },
  { key: "comunicados", label: "Comunicados internos", desc: "Avisos e comunicados da empresa", ready: false },
];

const STATUS_TONE = { active: "pos", suspended: "warn", revoked: "neg", terminated: "neg", inactive: "muted" };
const STATUS_PT = { active: "Ativo", suspended: "Suspenso", revoked: "Revogado", terminated: "Desligado", inactive: "Inativo" };

const PERM_LABEL = {
  "pending_questions.view": "Ver pendências",
  "pending_questions.answer": "Responder pendências",
  "pending_questions.assign": "Atribuir pendências",
  "pending_questions.dismiss": "Descartar pendências",
  "knowledge.create_from_answer": "Criar conhecimento a partir de respostas",
  "knowledge.approve": "Aprovar conhecimento",
  "conversation.takeover": "Assumir conversa (1:1)",
  "broadcast.send": "Disparar campanhas",
};
const RESTRICTED_LABEL = {
  executive: "Diretoria", finance_restricted: "Financeiro (restrito)", hr_restricted: "RH (restrito)",
  legal_restricted: "Jurídico (restrito)", commercial_restricted: "Comercial (restrito)",
  administrative_restricted: "Administrativo (restrito)", operations_restricted: "Operações (restrito)",
  compliance_only: "Compliance",
};

const CARGO_LEGEND = [
  { label: "CEO", tone: "primary", desc: "Acesso total. Recebe toda a inteligência de negócio (faturamento, pipeline, contas) pela ISA no WhatsApp e também opera o console. Topo da hierarquia." },
  { label: "Admin", tone: "primary", desc: "Opera o console: alimenta o cérebro, gerencia pessoas e permissões, dispara campanhas e configura integrações. Não vê dados de negócio no console." },
  { label: "Gestor", tone: "primary", desc: "Lidera um setor. Enxerga o conhecimento da sua área e pode receber permissões extras (responder pendências, campanhas)." },
  { label: "Representante", tone: "muted", desc: "Colaborador da linha de frente. Fala com a ISA no WhatsApp e enxerga apenas o que o seu setor e os acessos concedidos permitem." },
];

/* -------------------- base field primitives -------------------- */
function Field({ label, hint, icon: Icon, iconClass = "text-slate-400", children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
        {Icon && <Icon size={13} strokeWidth={2} className={iconClass} />}
        {label}
      </label>
      {children}
      {hint && <span className="text-[11px] leading-tight text-slate-400">{hint}</span>}
    </div>
  );
}

function Select({ className = "", children, ...p }) {
  return (
    <div className="relative">
      <select
        className={`w-full appearance-none rounded-xl border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 transition-[border-color,box-shadow] duration-200 focus:border-[#0b1220]/40 focus:outline-none focus:ring-4 focus:ring-[#0b1220]/5 ${className}`}
        {...p}
      >
        {children}
      </select>
      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

/* -------------------- reusable field: Setor -------------------- */
function SetorSelect({ value, onChange, testid = "emp-setor" }) {
  const known = DEPT_OPTIONS.map((d) => d.value);
  const [other, setOther] = useState(!!value && !known.includes(value));
  return (
    <div className="space-y-2">
      <Select data-testid={testid} value={other ? "__other__" : (value || "")}
        onChange={(e) => {
          if (e.target.value === "__other__") { setOther(true); onChange(""); }
          else { setOther(false); onChange(e.target.value); }
        }}>
        <option value="" disabled>Selecione o setor…</option>
        {DEPT_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        <option value="__other__">Outro (digitar)…</option>
      </Select>
      {other && (
        <Input data-testid={`${testid}-custom`} placeholder="Nome do novo setor (ex: Logística)"
          value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

/* -------------------- reusable field: Recebe no WhatsApp (por setor) -------------------- */
function NotifyTopics({ value = [], onChange }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="space-y-1 rounded-xl border border-slate-200 bg-slate-50/60 p-2" data-testid="notify-topics">
      {NOTIFY_SECTORS.map((s) => {
        const checked = value.includes(s.key);
        return (
          <label key={s.key}
            className={`flex items-start gap-3 rounded-lg px-2 py-2 transition-colors ${s.ready ? "cursor-pointer hover:bg-white" : "opacity-60"}`}
            data-testid={`notify-${s.key}`}>
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-indigo-600" checked={checked} disabled={!s.ready}
              onChange={() => s.ready && toggle(s.key)} data-testid={`notify-check-${s.key}`} />
            <span className="min-w-0">
              <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                {s.label} {!s.ready && <Badge tone="muted">Em breve</Badge>}
              </span>
              <span className="block text-[11px] leading-tight text-slate-400">{s.desc}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

function TenderOnlyToggle({ value, onChange, testid }) {
  return (
    <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${value ? "border-amber-300 bg-amber-50/70" : "border-slate-200 bg-slate-50/60 hover:bg-white"}`}
      data-testid={testid}>
      <input type="checkbox" className="mt-0.5 h-4 w-4 accent-amber-600" checked={!!value}
        onChange={(e) => onChange(e.target.checked)} data-testid={`${testid}-check`} />
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-sm font-medium text-slate-800"><Gavel size={14} className="text-amber-600" /> Somente licitações</span>
        <span className="block text-[11px] leading-tight text-slate-500">Recebe o radar diário e os alertas de licitação junto com o Comercial. A ISA nunca cobra, não pede localização nem trata como vendedor. Pode perguntar sobre licitações.</span>
      </span>
    </label>
  );
}

/* -------------------- Legenda -------------------- */
function LegendCard() {
  const [open, setOpen] = useState(false);
  return (
    <Card className="overflow-hidden">
      <button data-testid="legend-toggle" onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-5 py-4 text-left transition-colors hover:bg-slate-50/60">
        <span className="flex items-center gap-2 font-medium text-slate-900"><Info size={16} className="text-indigo-600" /> Legenda: o que cada cargo, setor e permissão significa</span>
        <ChevronDown size={18} className={`text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-5 border-t border-slate-100 px-5 py-5" data-testid="legend-body">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Cargos (nível de acesso)</p>
            <div className="space-y-2">
              {CARGO_LEGEND.map((c) => (
                <div key={c.label} className="flex gap-3 text-sm">
                  <span className="mt-0.5 shrink-0"><Badge tone={c.tone}>{c.label}</Badge></span>
                  <span className="text-slate-600">{c.desc}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Setor / Departamento</p>
            <p className="text-sm text-slate-600">Define a área da pessoa e qual conhecimento por setor ela enxerga (ex.: quem é do <b>Comercial</b> vê o conhecimento marcado como Comercial). Você pode criar novos setores usando a opção <b>“Outro”</b> no cadastro.</p>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Permissões funcionais (concedidas por pessoa)</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.values(PERM_LABEL).map((p) => <Badge key={p} tone="primary">{p}</Badge>)}
            </div>
            <p className="mt-2 text-xs text-slate-500">Essas permissões são <b>extras</b> e concedidas individualmente — não vêm automaticamente pelo cargo.</p>
          </div>
        </div>
      )}
    </Card>
  );
}

/* -------------------- Edit modal -------------------- */
function EditModal({ emp, onClose, onSaved }) {
  const [f, setF] = useState({
    name: emp.name || "", phone: emp.phone || "", email: emp.email || "",
    role: emp.role || "rep", department: emp.department || "commercial",
    birthdate: emp.birthdate || "", notify_topics: emp.notify_topics || [], tender_only: !!emp.tender_only,
  });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/employees/${emp.id}`, { ...f, birthdate: f.birthdate || null });
      toast.success("Funcionário atualizado");
      onSaved();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm" onClick={onClose} data-testid="edit-emp-modal">
      <Card className="w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-slate-900">Editar funcionário</h3>
          <button onClick={onClose} className="rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"><X size={18} /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <Field label="Nome completo">
            <Input data-testid="edit-name" placeholder="Ex: Fernando Lima" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="WhatsApp">
              <Input data-testid="edit-phone" placeholder="+55 63 9 9999-0000" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
            </Field>
            <Field label="E-mail">
              <Input data-testid="edit-email" placeholder="nome@empresa.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            </Field>
          </div>
          <Field label="Data de nascimento" icon={Cake} iconClass="text-pink-500" hint="Para enviar os parabéns automáticos.">
            <Input data-testid="edit-birthdate" type="date" value={f.birthdate || ""} onChange={(e) => setF({ ...f, birthdate: e.target.value })} />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Cargo">
              <Select data-testid="edit-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
                {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </Select>
            </Field>
            <Field label="Setor">
              <SetorSelect value={f.department} onChange={(v) => setF({ ...f, department: v })} testid="edit-setor" />
            </Field>
          </div>
          <Field label="Recebe no WhatsApp (por setor)" icon={Bell} iconClass="text-indigo-500"
            hint="Opt-in. Quem é do setor Comercial (inclusive o CEO) já recebe as licitações automaticamente.">
            <NotifyTopics value={f.notify_topics} onChange={(v) => setF({ ...f, notify_topics: v })} />
          </Field>
          <TenderOnlyToggle value={f.tender_only} onChange={(v) => setF({ ...f, tender_only: v })} testid="edit-tender-only" />
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <Btn type="button" variant="ghost" onClick={onClose}>Cancelar</Btn>
            <Btn data-testid="edit-save" disabled={busy}>{busy ? "Salvando…" : "Salvar alterações"}</Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}

/* -------------------- Funcionários -------------------- */
function DeleteEmpModal({ emp, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-zinc-900/40 p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="delete-emp-modal">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-slate-900">Excluir funcionário</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <p className="text-sm text-slate-600">
          Tem certeza que deseja excluir <b>{emp.name}</b>? O acesso ao WhatsApp e ao console é revogado
          imediatamente. O histórico que a pessoa gerou é preservado para auditoria.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={go} disabled={busy} data-testid="delete-emp-confirm"
            className="bg-red-600 text-white hover:bg-red-700">{busy ? "Excluindo…" : "Excluir"}</Btn>
        </div>
      </Card>
    </div>
  );
}

function Funcionarios({ emps, reload }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", role: "rep", department: "commercial", birthdate: "", notify_topics: [], tender_only: false });
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const add = async (e) => {
    e.preventDefault();
    if (!f.department) return toast.error("Escolha ou digite o setor");
    try {
      await api.post("/employees", { ...f, birthdate: f.birthdate || null, password: f.email ? "radar123" : undefined });
      toast.success("Funcionário adicionado (whitelist)");
      setF({ name: "", phone: "", email: "", role: "rep", department: "commercial", birthdate: "", notify_topics: [], tender_only: false });
      reload();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const del = async (emp) => {
    try {
      await api.delete(`/employees/${emp.id}`);
      toast.success(`${emp.name} foi excluído(a)`);
      setDeleting(null);
      reload();
    } catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="space-y-5">
      <LegendCard />

      <Card className="p-6">
        <div className="mb-5">
          <h3 className="font-display text-base font-semibold text-slate-900">Adicionar funcionário</h3>
          <p className="mt-1 text-sm text-slate-500">Cadastre a pessoa e libere o WhatsApp dela (whitelist). Só números cadastrados conseguem falar com a ISA.</p>
        </div>
        <form onSubmit={add} className="space-y-5">
          <div className="grid gap-x-4 gap-y-4 md:grid-cols-2 lg:grid-cols-3">
            <Field label="Nome completo">
              <Input data-testid="emp-name" placeholder="Ex: Fernando Lima" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
            </Field>
            <Field label="WhatsApp" hint="Com DDD. Só este número falará com a ISA.">
              <Input data-testid="emp-phone" placeholder="+55 63 9 9999-0000" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} required />
            </Field>
            <Field label="E-mail" hint="Opcional — dá acesso ao console.">
              <Input data-testid="emp-email" placeholder="nome@empresa.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            </Field>
            <Field label="Data de nascimento" icon={Cake} iconClass="text-pink-500" hint="Habilita os parabéns automáticos.">
              <Input data-testid="emp-birthdate" type="date" value={f.birthdate} onChange={(e) => setF({ ...f, birthdate: e.target.value })} />
            </Field>
            <Field label="Cargo">
              <Select data-testid="emp-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
                {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </Select>
            </Field>
            <Field label="Setor">
              <SetorSelect value={f.department} onChange={(v) => setF({ ...f, department: v })} />
            </Field>
          </div>
          <Field label="Recebe no WhatsApp (por setor)" icon={Bell} iconClass="text-indigo-500"
            hint="Opt-in. Quem é do setor Comercial (inclusive o CEO) já recebe as licitações automaticamente.">
            <NotifyTopics value={f.notify_topics} onChange={(v) => setF({ ...f, notify_topics: v })} />
          </Field>
          <TenderOnlyToggle value={f.tender_only} onChange={(v) => setF({ ...f, tender_only: v })} testid="emp-tender-only" />
          <div className="flex justify-end border-t border-slate-100 pt-4">
            <Btn data-testid="emp-submit"><UserPlus size={16} /> Adicionar funcionário</Btn>
          </div>
        </form>
      </Card>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {emps.map((e) => (
          <Card key={e.id} className="flex items-center justify-between p-4 hover:shadow-md" data-testid={`emp-${e.id}`}>
            <div className="min-w-0">
              <p className="truncate font-medium text-slate-900">{e.name}</p>
              <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><Phone size={12} /> {e.phone || "—"}</p>
              <p className="mt-1 flex items-center gap-1 text-xs text-slate-400"><Building2 size={11} /> {e.role === "owner" ? "Todos os setores" : deptLabel(e.department)}</p>
              {e.role === "owner" && (
                <p className="mt-1 flex items-center gap-1 text-[11px] font-medium text-emerald-600" data-testid={`emp-ceo-${e.id}`}>
                  <ShieldCheck size={11} /> Acesso total — tudo que pedir à ISA
                </p>
              )}
              {e.tender_only && (
                <p className="mt-1 flex items-center gap-1 text-[11px] font-medium text-amber-600" data-testid={`emp-tender-only-${e.id}`}>
                  <Gavel size={11} /> Somente licitações
                </p>
              )}
              {e.notify_topics?.length > 0 && (
                <p className="mt-1 flex items-center gap-1 text-[11px] text-indigo-500" data-testid={`emp-notify-${e.id}`}>
                  <Bell size={11} /> Recebe: {e.notify_topics.map((k) => (NOTIFY_SECTORS.find((s) => s.key === k)?.label || k)).join(", ")}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <Badge tone={e.role === "rep" ? "muted" : "primary"}>{ROLE_LABEL[e.role] || e.role}</Badge>
              <button onClick={() => setEditing(e)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-indigo-50 hover:text-indigo-600" data-testid={`emp-edit-${e.id}`}><Pencil size={15} /></button>
              <button onClick={() => setDeleting(e)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600" data-testid={`emp-del-${e.id}`}><Trash2 size={16} /></button>
            </div>
          </Card>
        ))}
      </div>

      {editing && <EditModal emp={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {deleting && <DeleteEmpModal emp={deleting} onClose={() => setDeleting(null)} onConfirm={() => del(deleting)} />}
    </div>
  );
}

/* -------------------- Permissões -------------------- */
function Permissoes({ emps }) {
  const active = emps.filter((e) => (e.status || "active") === "active");
  return (
    <div className="space-y-5">
      <Card className="flex items-start gap-3 border-indigo-100 bg-indigo-50/60 p-4">
        <ShieldCheck size={18} className="mt-0.5 shrink-0 text-indigo-600" />
        <p className="text-sm leading-relaxed text-slate-700">
          <b>Default-deny:</b> pertencer à empresa não basta. Cada pessoa só enxerga o que o cargo, o setor e os
          <b> acessos concedidos</b> permitem. Aqui você vê exatamente quem enxerga o quê.
        </p>
      </Card>

      <LegendCard />

      <div className="grid gap-3 md:grid-cols-2">
        {active.map((e) => {
          const isCEO = e.role === "owner";
          return (
          <Card key={e.id} className="p-5 hover:shadow-md" data-testid={`perm-${e.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="font-medium text-slate-900">{e.name}</p>
              <Badge tone={STATUS_TONE[e.status || "active"]}>{STATUS_PT[e.status || "active"] || e.status}</Badge>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
              <span className="flex items-center gap-1"><UserCog size={12} /> {ROLE_LABEL[e.role] || e.role}</span>
              <span className="flex items-center gap-1"><Building2 size={12} /> {isCEO ? "Todos os setores" : deptLabel(e.department)}</span>
              {e.email && <span className="flex items-center gap-1"><Mail size={12} /> {e.email}</span>}
            </div>
            {isCEO && (
              <div className="mt-3 flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50/70 px-3 py-2" data-testid={`perm-ceo-${e.id}`}>
                <ShieldCheck size={15} className="mt-0.5 shrink-0 text-emerald-600" />
                <p className="text-xs leading-relaxed text-slate-700">
                  <b>Acesso total (CEO).</b> Enxerga todos os setores e recebe pela ISA tudo que pedir —
                  auditoria, agenda de todo mundo, funil e números da empresa. Sem restrições.
                </p>
              </div>
            )}
            <div className="mt-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Permissões funcionais</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {isCEO
                  ? <Badge tone="pos">Todas (acesso irrestrito)</Badge>
                  : (e.permissions || []).length
                    ? e.permissions.map((p) => <Badge key={p} tone="primary">{PERM_LABEL[p] || p}</Badge>)
                    : <span className="text-xs text-slate-400">Nenhuma (apenas o padrão do cargo)</span>}
              </div>
            </div>
            <div className="mt-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Acesso a domínios restritos</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {isCEO
                  ? <Badge tone="pos">Todos os setores e recursos</Badge>
                  : (e.restricted_access || []).length
                    ? e.restricted_access.map((r) => <Badge key={r} tone="warn">{RESTRICTED_LABEL[r] || r}</Badge>)
                    : <span className="text-xs text-slate-400">Nenhum acesso restrito concedido</span>}
              </div>
            </div>
          </Card>
          );
        })}
      </div>
    </div>
  );
}

export default function Team() {
  const [emps, setEmps] = useState([]);
  const [tab, setTab] = useState("funcionarios");
  const load = () => api.get("/employees").then(({ data }) => setEmps(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const tabs = [
    { key: "funcionarios", label: "Funcionários", icon: Users },
    { key: "permissoes", label: "Permissões", icon: ShieldCheck },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Pessoas &amp; Permissões</h1>
        <p className="mt-1 text-sm text-slate-500">O cérebro das pessoas: quem está na empresa, quem fala com a ISA e quem enxerga o quê.</p>
      </div>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />
      {tab === "funcionarios" && <Funcionarios emps={emps} reload={load} />}
      {tab === "permissoes" && <Permissoes emps={emps} />}
    </div>
  );
}
